import { Container, Graphics, Ticker } from 'pixi.js';
import { DefaultRandom, type Random } from '../core/Random';

/**
 * Lightweight particle burst for win moments. Spawns N small circles at the
 * given world point, gives them random radial velocities + slight gravity,
 * fades them out, and removes them on completion.
 *
 * <p>Pixi-particle-emitter (already in cs-core's deps) gives richer effects
 * but is overkill for the v0 thunk; this is ~80 lines and zero new deps.
 * Upgrade later if/when we want spritesheets, tinted gradients, etc.
 */
export class WinParticles extends Container {
  private particles: ParticleState[] = [];
  private tickHandler: ((tick: Ticker) => void) | null = null;

  constructor(
    private readonly ticker: Ticker,
    private readonly random: Random = new DefaultRandom(),
  ) {
    super();
  }

  /** Spawn {@code count} particles around the local origin (place this Container
   * where you want the burst origin). Auto-stops when all particles fade. */
  burst(count: number, options: BurstOptions = {}): void {
    const tints = options.tints ?? [0xff6bd6, 0x42e2f5, 0xfff59d, 0xff8a3d];
    const minSpeed = options.minSpeed ?? 180;
    const maxSpeed = options.maxSpeed ?? 420;
    const lifeMs = options.lifeMs ?? 1100;
    const radius = options.radius ?? 5;
    const gravity = options.gravity ?? 600;

    for (let i = 0; i < count; i++) {
      const angle = this.random.nextFloat() * Math.PI * 2;
      const speed = minSpeed + this.random.nextFloat() * (maxSpeed - minSpeed);
      const tint = tints[this.random.nextIntBetween(0, tints.length)] as number;

      const sprite = new Graphics();
      sprite.circle(0, 0, radius).fill({ color: tint });
      this.addChild(sprite);

      this.particles.push({
        sprite,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        ageMs: 0,
        lifeMs,
        gravity,
      });
    }

    if (!this.tickHandler) {
      this.tickHandler = (tick) => this.advance(tick.deltaMS);
      this.ticker.add(this.tickHandler);
    }
  }

  destroyEffect(): void {
    if (this.tickHandler) {
      this.ticker.remove(this.tickHandler);
      this.tickHandler = null;
    }
    for (const p of this.particles) {
      p.sprite.destroy();
    }
    this.particles = [];
  }

  private advance(deltaMs: number): void {
    const seconds = deltaMs / 1000;
    const next: ParticleState[] = [];
    for (const p of this.particles) {
      p.ageMs += deltaMs;
      if (p.ageMs >= p.lifeMs) {
        p.sprite.destroy();
        continue;
      }
      p.sprite.x += p.vx * seconds;
      p.sprite.y += p.vy * seconds;
      p.vy += p.gravity * seconds;
      p.sprite.alpha = 1 - p.ageMs / p.lifeMs;
      next.push(p);
    }
    this.particles = next;

    if (this.particles.length === 0 && this.tickHandler) {
      this.ticker.remove(this.tickHandler);
      this.tickHandler = null;
    }
  }
}

interface ParticleState {
  sprite: Graphics;
  vx: number;
  vy: number;
  ageMs: number;
  lifeMs: number;
  gravity: number;
}

export interface BurstOptions {
  tints?: readonly number[];
  minSpeed?: number;
  maxSpeed?: number;
  lifeMs?: number;
  radius?: number;
  gravity?: number;
}
